import { useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'antd'
import { disconnectSinglePlatform } from '@/features/platform/api'

type ResultRow = { id: string; name: string; success: boolean; message?: string }

type Props = {
  open: boolean
  ids: string[]
  isBulk: boolean
  idToName: Record<string, string>
  onClose: () => void
}

export function BulkDisconnectProgressModal({ open, ids, isBulk, idToName, onClose }: Props) {
  const [pct, setPct] = useState(0)
  const [results, setResults] = useState<ResultRow[]>([])
  const runKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open || ids.length === 0) return
    const key = `${isBulk}-${ids.join(',')}`
    if (runKeyRef.current === key) return
    runKeyRef.current = key
    setPct(0)
    setResults([])
    const total = ids.length
    ;(async () => {
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]
        if (id == null) continue
        const name = idToName[id] ?? id
        try {
          await disconnectSinglePlatform(id, isBulk)
          setResults((prev) => [...prev, { id, name, success: true }])
        } catch (e) {
          const message = e instanceof Error ? e.message : 'Failed'
          setResults((prev) => [...prev, { id, name, success: false, message }])
        }
        setPct(Math.round(((i + 1) / total) * 100))
      }
    })()
    return () => {
      runKeyRef.current = null
    }
  }, [idToName, ids, isBulk, open])

  return (
    <Modal
      open={open}
      title="Bulk disconnect"
      footer={
        pct === 100 ? (
          <Button type="primary" danger onClick={onClose}>
            Close
          </Button>
        ) : (
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-gray-500">Do not close or refresh this page.</span>
            <Button danger onClick={onClose}>
              Cancel
            </Button>
          </div>
        )
      }
      closable={pct === 100}
      onCancel={pct === 100 ? onClose : undefined}
    >
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div className="h-full rounded-full bg-brand-500 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-sm font-medium text-gray-700">{pct}% completed</p>
        </div>
        <div className="max-h-48 space-y-1 overflow-y-auto">
          {results.map((r) => (
            <div key={r.id} className="text-sm">
              {r.success ? (
                <span className="text-green-700">{r.name} - Success</span>
              ) : (
                <span className="text-red-600">
                  {r.name} - {r.message ?? 'Failed'}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}
